import { useEffect } from 'react';
import { detectSiteMode } from './site';

export type PageSeoInput = {
  title?: string;
  description?: string;
  /** Absolute path only, e.g. /tools/chatgpt */
  path?: string;
  image?: string;
  /** Optional JSON-LD object; replaced on each update */
  jsonLd?: Record<string, unknown> | null;
};

const SITE_BRAND: Record<'CN' | 'GLOBAL', string> = {
  CN: 'aihomehub.cn',
  GLOBAL: 'GlobalAI Tools',
};

const DEFAULT_TITLE: Record<'CN' | 'GLOBAL', string> = {
  CN: 'AI速览局 - aihomehub.cn | 按任务选 AI 工具',
  GLOBAL: 'GlobalAI Tools | Pick AI tools by job',
};

const DEFAULT_DESC: Record<'CN' | 'GLOBAL', string> = {
  CN: '按任务选 AI 工具，并标注国内能否直连。写作、出图、视频、编程对照短文。',
  GLOBAL: 'Pick AI tools by job, with clear vendor links and mainland China access notes.',
};

const DEFAULT_OG: Record<'CN' | 'GLOBAL', string> = {
  CN: 'https://aihomehub.cn/og-image.png',
  GLOBAL: 'https://globalaitool.com/assets/og-image.png',
};

function upsertMeta(attr: 'name' | 'property', key: string, content: string): void {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertCanonical(href: string): void {
  let link = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
}

function upsertJsonLd(data: Record<string, unknown> | null): void {
  const id = 'page-seo-jsonld';
  const existing = document.getElementById(id);
  if (!data) {
    existing?.remove();
    return;
  }
  const el = existing || document.createElement('script');
  el.id = id;
  el.setAttribute('type', 'application/ld+json');
  el.textContent = JSON.stringify(data);
  if (!existing) document.head.appendChild(el);
}

function toAbsoluteUrl(url: string, origin: string): string {
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('//')) return `${window.location.protocol}${url}`;
  return `${origin}${url.startsWith('/') ? url : `/${url}`}`;
}

/**
 * 更新当前页 title / description / canonical / OG，离开时恢复站点默认值。
 */
export function usePageSeo(seo: PageSeoInput): void {
  const site = detectSiteMode();
  const brand = SITE_BRAND[site];

  useEffect(() => {
    const title = seo.title
      ? (seo.title.includes(brand) ? seo.title : `${seo.title} | ${brand}`)
      : DEFAULT_TITLE[site];
    const description = (seo.description || DEFAULT_DESC[site]).slice(0, 180);
    const origin = `${window.location.protocol}//${window.location.host}`;
    const canonical = seo.path ? `${origin}${seo.path.startsWith('/') ? seo.path : `/${seo.path}`}` : `${origin}${window.location.pathname}`;
    const image = toAbsoluteUrl(seo.image || DEFAULT_OG[site], origin);

    document.title = title;
    upsertMeta('name', 'description', description);
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', canonical);
    upsertMeta('property', 'og:image', image);
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', image);
    upsertCanonical(canonical);
    upsertJsonLd(seo.jsonLd || null);

    return () => {
      document.title = DEFAULT_TITLE[site];
      upsertMeta('name', 'description', DEFAULT_DESC[site]);
      upsertCanonical(`${origin}/`);
      upsertJsonLd(null);
    };
    // jsonLd compared by value to avoid object-identity thrash
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site, brand, seo.title, seo.description, seo.path, seo.image, JSON.stringify(seo.jsonLd || null)]);
}
